import React, { memo, useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import { fetchPatchPermission } from "Slices/intersections";

import { ActivePermission } from "./ActivePermission";
import {
  IntersectionDescStyled,
  IntersectionExpandDetailStyled,
  IntersectionItemWrapperStyled,
  IntersectionItemColumnsStyled,
  IntersectionItemDetailToggleStyled,
  IntersectionSubTitleStyled,
  IntersectionFooterStyled,
  RefreshButtonStyled,
} from "./styles";
import { IntersectionNamePermission } from "../utils";
import { ReactComponent as BlockedIcon } from "../assets/blockedIcon.svg";
import { ReactComponent as ClearIcon } from "../assets/clearedIcon.svg";
import { ReactComponent as ArrowDownIcon } from "../../common/assets/selectIcon.svg";
import { ReactComponent as RefreshIcon } from "../assets/refreshIcon.svg";

interface IntersectionItemProps {
  data: IntersectionNamePermission;
  onRefresh: () => void;
  lastUpdatedAt?: number;
}

export const IntersectionItem = memo(
  ({ data, onRefresh, lastUpdatedAt }: IntersectionItemProps) => {
    const [showDetail, setShowDetail] = useState(false);
    const dispatch = useDispatch();
    const [timeAgoText, setTimeAgoText] = useState<string>("just now");

    const updateTimeAgoText = useCallback(() => {
      if (!lastUpdatedAt) return "unknown";

      const now = Date.now();
      const diffInMs = now - lastUpdatedAt;
      const diffInSeconds = Math.floor(diffInMs / 1000);
      const diffInMinutes = Math.floor(diffInMs / (1000 * 60));

      if (diffInSeconds < 1) return "just now";
      if (diffInMinutes < 1) return `${diffInSeconds} seconds ago`;
      if (diffInMinutes === 1) return "1 minute ago";
      return `${diffInMinutes} minutes ago`;
    }, [lastUpdatedAt]);

    useEffect(() => {
      if (!lastUpdatedAt) return;

      setTimeAgoText(updateTimeAgoText());

      let timeoutId: number;
      const scheduleUpdate = () => {
        const now = Date.now();
        const diffInMs = now - lastUpdatedAt;
        const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
        // Update the time ago text
        setTimeAgoText(updateTimeAgoText());
        // Determine the next update delay
        const nextDelay =
          diffInMinutes < 1
            ? 15000 - (diffInMs % 15000)
            : 60000 - (diffInMs % 60000);
        // Schedule the next update
        timeoutId = setTimeout(scheduleUpdate, nextDelay);
      };
      // Start the update loop
      scheduleUpdate();
      return () => clearTimeout(timeoutId);
    }, [lastUpdatedAt, updateTimeAgoText]);

    const { id, name, activePermissions } = data;
    const totalActive = activePermissions.length;
    return (
      <IntersectionItemWrapperStyled key={id}>
        <IntersectionItemColumnsStyled>
          <IntersectionItemColumnsStyled>
            {totalActive === 0 ? <ClearIcon /> : <BlockedIcon />}
            <div>
              <div>{name}</div>
              <IntersectionDescStyled>
                {totalActive === 0 ? "Cleared" : `${totalActive} active`}
              </IntersectionDescStyled>
            </div>
          </IntersectionItemColumnsStyled>
          {totalActive > 0 && (
            <IntersectionItemDetailToggleStyled active={showDetail}>
              Detail{" "}
              <ArrowDownIcon onClick={() => setShowDetail(!showDetail)} />
            </IntersectionItemDetailToggleStyled>
          )}
        </IntersectionItemColumnsStyled>
        {showDetail && activePermissions.length > 0 && (
          <IntersectionExpandDetailStyled>
            <IntersectionSubTitleStyled>
              Active Permissions
            </IntersectionSubTitleStyled>
            {activePermissions.map((each) => {
              const { id, intersection_id, vehicle_id } = each;
              return (
                <ActivePermission
                  key={id}
                  data={each}
                  onClickHandler={() => {
                    const payload = {
                      intersection_id,
                      entry_permission_id: id,
                      vehicle_id,
                      successMessage:
                        "The entry permission has been successfully revoked",
                    };
                    dispatch(fetchPatchPermission(payload));
                  }}
                />
              );
            })}
          </IntersectionExpandDetailStyled>
        )}
        <IntersectionFooterStyled>
          <span>Last updated: {timeAgoText}</span>
          <RefreshButtonStyled onClick={onRefresh}>
            <RefreshIcon /> Refresh
          </RefreshButtonStyled>
        </IntersectionFooterStyled>
      </IntersectionItemWrapperStyled>
    );
  }
);
